import { routePage, TRoutePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { domainUserDisplayName, emailSuffix, userDisplayName } from 'src/app/infrastructure/functions/user-name.function';
import { navCategory, TNavCategory } from './nav-history-entry';

// Beschrijft hoe uit een resolved detail-DTO een history-entry wordt afgeleid. Alleen
// pagina's die hier staan worden geregistreerd; overige detailpagina's negeren we.
export interface INavDescriptor {
    category: TNavCategory;
    // Optioneel: categorie per DTO, voor routes die meerdere entiteit-soorten tonen (Page-detail toont
    // zowel Pages als Tabs, onderscheiden via isTab). Ontbreekt ⇒ `category`.
    categoryOf?: (dto: any) => TNavCategory;
    label: (dto: any) => string;
    photoGuid: (dto: any) => string | null;
    logoFolderShard: (dto: any) => string | null;
    logoVersion: (dto: any) => string | null;
    // Alleen relevant voor domainUsers — zie INavHistoryEntry.photoIsUserFallback.
    photoIsUserFallback: (dto: any) => boolean;
    // Alleen relevant voor domainUsers/projectMembers — zie INavHistoryEntry.email.
    email: (dto: any) => string | null;
    // Optioneel: ouder van een pagina, zie INavHistoryEntry.parentGuid. Zonder category van hetzelfde soort (taak in een
    // taak), met category van een ander soort (melding in een taak). De ouder wordt bij het vastleggen ook als entry
    // gegarandeerd (label uit de DTO), zodat het kind iets heeft om onder te hangen.
    parent?: (dto: any) => { guid: string; label: string; category?: TNavCategory } | null;
}

// Generieke foto-guid: thumb uit de photoBox, anders de photoBox-guid, anders een
// los photoGuid-veld. Null ⇒ leeg kader in de ImageBox.
const __photoGuid = (dto: any): string | null =>
    dto?.photoBoxDto?.photoThumbDto?.guid
    ?? dto?.photoBoxDto?.guid
    ?? dto?.photoGuid
    ?? null;

// DomainUser levert zijn hoofdfoto niet via photoBoxDto/photoGuid (de generieke fallback hierboven),
// maar via twee losse velden: profilePhotoDto (domain-specifieke keuze, DomainUser.ProfileFileBoxId)
// met fallback userPhotoDto (de globale Home-hoofdfoto, User.PhotoFileBoxId) — zelfde patroon als
// domain-user-data.ts/domain-user-list-data.ts.
const __domainUserPhotoGuid = (dto: any): string | null =>
    dto?.profilePhotoDto?.photoThumbDto?.guid
    ?? dto?.userPhotoDto?.photoThumbDto?.guid
    ?? null;

// True zolang er geen eigen domain-specifieke profielfoto gekozen is (profilePhotoDto leeg) — dan
// komt __domainUserPhotoGuid hierboven via de userPhotoDto-fallback tot stand, zie
// INavHistoryEntry.photoIsUserFallback.
const __domainUserPhotoIsUserFallback = (dto: any): boolean => !dto?.profilePhotoDto?.photoThumbDto?.guid;

// DomainUser-DTO heeft geen displayName en DomainUser.Name is meestal leeg — zonder deze keten viel
// het label terug op de guid (zie _TabPageRouteComponent: `label(dto) || guid`). Zelfde keten als
// domain-user-data.ts: name → applicationName → firstName + lastName → email.
const __domainUserLabel = (dto: any): string =>
    domainUserDisplayName(dto?.name, userDisplayName(dto?.userDto));

// ProjectMember: zelfde keten, maar DomainUser.Name zit genest in domainUserDto (userDto staat wel
// direct op de DTO) — zie project-member-data.ts.
const __projectMemberLabel = (dto: any): string =>
    domainUserDisplayName(dto?.domainUserDto?.name, userDisplayName(dto?.userDto));

// E-mailadres van de User achter dit DomainUser-record — getoond als sublabel onder de naam in de
// drawer (zie NavigationHistoryService.__itemNode / INavTreeNode.sublabel). Leeg als de naam al het
// e-mailadres is (einde van de keten), zelfde regel als de emailSuffix-pipe.
const __domainUserEmail = (dto: any): string | null =>
    emailSuffix(__domainUserLabel(dto), dto?.userDto?.email) || null;

// Idem voor ProjectMember (zelfde userDto, label via __projectMemberLabel).
const __projectMemberEmail = (dto: any): string | null =>
    emailSuffix(__projectMemberLabel(dto), dto?.userDto?.email) || null;

// Generiek label: de meest voorkomende weergavevelden, met nummer als laatste redmiddel.
const __label = (dto: any): string =>
    dto?.name ?? dto?.displayName ?? dto?.description ?? (dto?.nr != null ? `#${dto.nr}` : '');

// De meeste categorieën hebben geen logo (alleen domains) — standaard null.
const __noLogo = (): string | null => null;

// Domain-logo's zijn kale publieke statische bestanden (zie backend UseStaticFiles): de
// folderShard is het enige domein-specifieke ingrediënt voor de URL, zie
// DomainProfileService.domainLogoThumb_url.
const __logoFolderShard = (dto: any): string | null => dto?.logosDto?.folderShard ?? null;

// modifiedOn van het logo-bestand zelf: cache-bust zodra iemand na dit bezoek een nieuw
// logo uploadt (zelfde patroon als DomainProfileService.logo_bump).
const __logoVersion = (dto: any): string | null =>
    dto?.logosDto?.logoThumbDto?.modifiedOn ?? dto?.logosDto?.logoDto?.modifiedOn ?? null;

const __noUserFallback = (): boolean => false;
const __noEmail = (): string | null => null;

const __make = (
    category: TNavCategory,
    label: (dto: any) => string = __label,
    photoGuid: (dto: any) => string | null = __photoGuid,
    logoFolderShard: (dto: any) => string | null = __noLogo,
    logoVersion: (dto: any) => string | null = __noLogo,
    photoIsUserFallback: (dto: any) => boolean = __noUserFallback,
    email: (dto: any) => string | null = __noEmail,
): INavDescriptor => ({ category, label, photoGuid, logoFolderShard, logoVersion, photoIsUserFallback, email });

export const navHistoryDescriptors: Partial<Record<TRoutePage, INavDescriptor>> = {
    // admin-niveau
    // Tabs zijn Page-rijen (Kind='tab') en openen dezelfde Page-detailroute — zonder categoryOf
    // belandde elk Tab-bezoek onder de "pages"-kop i.p.v. onder AdminPanel's "tabs"-tab.
    [routePage.page]: { ...__make(navCategory.pages), categoryOf: dto => dto?.isTab ? navCategory.tabs : navCategory.pages },
    [routePage.function]: __make(navCategory.functions),
    [routePage.adminRole]: __make(navCategory.roles),
    [routePage.adminActivity]: __make(navCategory.activities),
    [routePage.adminPageComponent]: __make(navCategory.pageComponents),
    [routePage.adminClaim]: __make(navCategory.claims),
    [routePage.adminClaimAction]: __make(navCategory.claimActions),
    [routePage.adminCreditType]: __make(navCategory.creditTypes),
    [routePage.theme]: __make(navCategory.themes),
    // user-niveau 1/2
    [routePage.domain]: __make(navCategory.domains, __label, __photoGuid, __logoFolderShard, __logoVersion),
    [routePage.project]: __make(navCategory.projects),
    [routePage.domainUser]: __make(navCategory.domainUsers, __domainUserLabel, __domainUserPhotoGuid, __noLogo, __noLogo, __domainUserPhotoIsUserFallback, __domainUserEmail),
    [routePage.contact]: __make(navCategory.contacts),
    [routePage.contactType]: __make(navCategory.contactTypes, d => d?.description ?? __label(d)),
    [routePage.observationType]: __make(navCategory.observationTypes, d => d?.description ?? __label(d)),
    [routePage.taskType]: __make(navCategory.taskTypes, d => d?.description ?? __label(d)),
    [routePage.category]: __make(navCategory.projectCategories, d => d?.description ?? __label(d)),
    // user-niveau 3 (project-childs)
    // ProjectMember levert de foto net als DomainUser via profilePhotoDto/userPhotoDto. photoIsUserFallback
    // blijft false: userPhotoGuid_propagate werkt alleen domainUsers-entries bij.
    [routePage.projectMember]: __make(navCategory.projectMembers, __projectMemberLabel, __domainUserPhotoGuid, __noLogo, __noLogo, __noUserFallback, __projectMemberEmail),
    // Melding in een taak (fase 3c): onder de tab notices van die taak.
    [routePage.notice]: {
        ...__make(navCategory.notices, d => d?.subject ?? __label(d)),
        parent: d => d?.task_Guid ? { guid: d.task_Guid, label: d.task_Subject ?? '', category: navCategory.tasks } : null,
    },
    [routePage.task]: {
        ...__make(navCategory.tasks, d => d?.subject ?? __label(d)),
        parent: d => d?.parentTask_Guid ? { guid: d.parentTask_Guid, label: d.parentTask_Subject ?? '' } : null,
    },
};

// Omgekeerde van de registry: de pagina achter een categorie (tabPreset van de tabs onder een bezochte pagina in de
// drawer). Tabs zijn Page-rijen en openen de Page-detailroute (zie categoryOf hierboven).
export const navCategoryRoutePage: Partial<Record<TNavCategory, TRoutePage>> = {
    ...Object.fromEntries(Object.entries(navHistoryDescriptors).map(([page, d]) => [d!.category, page as TRoutePage])),
    [navCategory.tabs]: routePage.page,
};
